import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	ActivationStart,
	ChildActivationStart,
	Router
} from '@angular/router';
import {
	NbLayoutModule,
	NbSidebarModule,
	NbSidebarService,
	NbSpinnerModule
} from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { ActionsFooterComponent } from './../actions-footer/actions-footer.component';
import { ActionsFooterService } from './../actions-footer/actions-footer.service';
import { BreadcrumbComponent } from './../breadcrumb/component/breadcrumbs.component';
import { FooterComponent } from './../footer/footer.component';
import { HeaderComponent } from './../header/header.component';
import { LoadingService } from './../loading.services';
import { LayoutPageTitleComponent } from './../page-title/layout-page-title.component';

@UntilDestroy()
@Component({
	selector: 'app-one-column-layout',
	styleUrls: ['./one-column.layout.scss'],
	template: `
		<nb-layout
			windowMode
			[restoreScrollTop]="true"
			[nbSpinner]="loadingService.spinner$ | async"
			nbSpinnerStatus="primary"
			nbSpinnerSize="giant"
		>
			<nb-layout-header role="banner" fixed>
				<app-header></app-header>
			</nb-layout-header>

			<nb-sidebar
				fixed
				start
				state="compacted"
				tag="menu-sidebar"
				(mouseover)="onMouseOverEvent()"
				(mouseout)="onMouseOutEvent()"
			>
				<ng-content select="nb-menu"></ng-content>
			</nb-sidebar>

			<nb-layout-column role="main">
				<app-breadcrumb></app-breadcrumb>
				<app-layout-page-title></app-layout-page-title>
				<ng-content select="router-outlet"></ng-content>
			</nb-layout-column>

			@if (actionsFooterService.actions.length) {
				<nb-layout-footer
					class="actions-footer"
					[class.expanded]="sidebarExpanded"
				>
					<app-actions-footer role="toolbar"></app-actions-footer>
				</nb-layout-footer>
			}

			<nb-layout-footer class="app-footer">
				<app-footer></app-footer>
			</nb-layout-footer>
		</nb-layout>
	`,
	standalone: true,
	imports: [
		NbLayoutModule,
		NbSpinnerModule,
		HeaderComponent,
		NbSidebarModule,
		BreadcrumbComponent,
		LayoutPageTitleComponent,
		ActionsFooterComponent,
		FooterComponent,
		AsyncPipe
	]
})
export class OneColumnLayoutComponent {
	sidebarExpanded = false;

	/**
	 * DI
	 */
	public actionsFooterService = inject(ActionsFooterService);
	public loadingService = inject(LoadingService);
	private sidebarService = inject(NbSidebarService);
	private router = inject(Router);

	constructor() {
		// this._listenOnSidebarState();
		// this._routerOnChangeListen();
	}

	onMouseOutEvent() {
		this.sidebarService.compact('menu-sidebar');
	}

	onMouseOverEvent() {
		this.sidebarService.expand('menu-sidebar');
	}

	private _routerOnChangeListen(): void {
		this.router.events
			.pipe(
				filter(
					event =>
						event instanceof ActivationStart ||
						event instanceof ChildActivationStart
				)
			)
			.subscribe((event: ActivationStart) => {
				if (
					this._routeHasResolve(event.snapshot) ||
					this._routeHasLoadingStart(event.snapshot)
				) {
					this.loadingService.show();
				} else {
					this.loadingService.hide();
				}
			});
	}

	private _routeHasLoadingStart(snapshot: ActivatedRouteSnapshot): boolean {
		return (
			snapshot.routeConfig?.data?.loading?.start === undefined ||
			snapshot.routeConfig?.data?.loading?.start === true
		);
	}

	private _routeHasResolve(snapshot: ActivatedRouteSnapshot): boolean {
		return !!(
			snapshot.routeConfig?.resolve &&
			Object.keys(snapshot.routeConfig?.resolve).length
		);
	}

	private _listenOnSidebarState(): void {
		this.sidebarService
			.onExpand()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.sidebarExpanded = true;
			});

		this.sidebarService
			.onCompact()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.sidebarExpanded = false;
			});

		this.sidebarService
			.onToggle()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.sidebarExpanded = !this.sidebarExpanded;
			});
	}
}
